export const RISK_STYLES = {
  "On Track": { fg: "#059669", bg: "#ECFDF5", label: "In Transit" },
  "Needs Attention": { fg: "#D97706", bg: "#FFFBEB", label: "Needs Attention" },
  "Delayed": { fg: "#DC2626", bg: "#FEF2F2", label: "Delayed" },
  "Ready to Close": { fg: "#059669", bg: "#ECFDF5", label: "Ready to Close" },
  "Missing Documents": { fg: "#D97706", bg: "#FFFBEB", label: "Missing Documents" },
  "Customer Update Due": { fg: "#D97706", bg: "#FFFBEB", label: "Customer Update Due" },
  "POD Missing": { fg: "#DC2626", bg: "#FEF2F2", label: "POD Missing" },
  "Closed": { fg: "#64748B", bg: "#F1F5F9", label: "Closed" },
  "Closeout Blocked": { fg: "#DC2626", bg: "#FEF2F2", label: "Closeout Blocked" },
};

// Single primary operational badge per job, by priority.
export function primaryStatus(job) {
  if (job.current_stage === "Closed") return "Closed";
  if (job.current_stage === "Ready to Close")
    return !job.can_close ? "Closeout Blocked" : "Ready to Close";
  const issues = job.issues || [];
  if (job.risk_status === "Delayed") return "Delayed";
  if (job.risk_status === "Needs Attention") return "Needs Attention";
  if (issues.some((i) => i.startsWith("Missing documents"))) return "Missing Documents";
  if (issues.includes("Customer update overdue")) return "Customer Update Due";
  if (issues.includes("Proof of delivery missing")) return "POD Missing";
  if (issues.length > 0) return "Needs Attention";
  return "On Track";
}

// Single source of truth: a job is "Ready to Close" iff it sits in that stage.
// Includes both ready-for-final-close AND closeout-blocked jobs.
export function readyToClose(job) {
  return job.current_stage === "Ready to Close";
}

// Closeout blocked: in Ready to Close but can't close, OR Delivered with unresolved
// blockers preventing advance. Mirrors backend _is_closeout_blocked / My Queue logic.
export function closeoutBlocked(job) {
  if (job.current_stage === "Ready to Close") return !job.can_close;
  if (job.current_stage === "Delivered") return (job.rtc_blockers || []).length > 0;
  return false;
}

export function needsAttention(job) {
  if (typeof job.needs_attention === "boolean") return job.needs_attention;
  if (job.current_stage === "Closed") return false;
  if (["Needs Attention", "Delayed"].includes(job.risk_status)) return true;
  return (job.issues || []).length > 0;
}

export function closeBlockShort(job) {
  if (job.close_short && job.close_short !== "Ready for final close") {
    const map = {
      "Proof of delivery received": "POD not received",
      "Final customer update sent": "Final update not sent",
      "Required documents complete": "Documents incomplete",
      "Internal notes complete": "Internal notes incomplete",
      "Billing handoff complete": "Billing handoff incomplete",
      "Manager review complete": "Manager review incomplete",
      "Resolve risk status": "Needs Attention unresolved",
      "Next action is overdue": "Next action overdue",
    };
    return map[job.close_short] || job.close_short;
  }
  if (!job.close_reason) return "Closeout blocked";
  return job.close_reason.replace(/^Cannot close:\s*/, "").replace(/\.$/, "");
}

export const DOC_STATUS_STYLES = {
  Received: { fg: "#059669", bg: "#ECFDF5" },
  Uploaded: { fg: "#059669", bg: "#ECFDF5" },
  Pending: { fg: "#D97706", bg: "#FFFBEB" },
  Missing: { fg: "#DC2626", bg: "#FEF2F2" },
  "Not Required": { fg: "#64748B", bg: "#F1F5F9" },
};

export function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export function isOverdue(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

export function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

// Predicates kept in lock-step with backend compute_issues / dashboard counts
export const BOARD_FILTERS = {
  active: (j) => j.current_stage !== "Closed",
  "pickup-today": (j) => ["Pickup Scheduled", "New Job"].includes(j.current_stage) && isToday(j.next_action_due_date),
  "delivery-today": (j) => isToday(j.delivery_due_date) && j.current_stage !== "Closed",
  "ready-to-close": (j) => readyToClose(j),
  "closeout-blocked": (j) => closeoutBlocked(j),
  closed: (j) => j.current_stage === "Closed",
};

export const BOARD_FILTER_LABELS = {
  active: "Active",
  "pickup-today": "Pickup Today",
  "delivery-today": "Delivery Today",
  "ready-to-close": "Ready to Close",
  "closeout-blocked": "Closeout Blocked",
  closed: "Closed",
};

export function dueLabel(iso) {
  if (!iso) return "—";
  const diff = new Date(iso).getTime() - Date.now();
  const days = Math.round(diff / 86400000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Due today";
  return `Due in ${days}d`;
}

export function suggestedUpdate(stage, risk) {
  if (risk === "Delayed")
    return "Your shipment is currently delayed due to a pending next action. Our team is working on it and will provide another update once the issue is cleared.";
  const map = {
    "New Job": "We have received your shipment request and are preparing the job. We will confirm pickup scheduling shortly.",
    "Pickup Scheduled": "Your shipment pickup has been scheduled. We will confirm once the carrier has collected the cargo.",
    "Picked Up": "Your shipment pickup has been confirmed and the job is now moving to the next stage. We will send another update once the shipment is in transit.",
    "In Transit": "Your shipment is in transit and on its scheduled route. We will notify you as it approaches the destination facility.",
    "At Facility": "Your shipment has arrived at our destination facility and is being prepared for final delivery.",
    "Ready for Delivery": "Your shipment is ready for delivery. We will coordinate the delivery appointment and confirm the time.",
    "Delivered": "Your shipment has been delivered. Proof of delivery will be shared as part of the closeout.",
    "Closed": "This shipment has been completed and closed. Thank you for working with us.",
  };
  return map[stage] || "We will provide an update on your shipment shortly.";
}
